/**
 * <TutorStudentProfilePage> — la fiche d'un élève, vue par son masteur (page).
 *
 * La route `student-profil` (`StudentProfil.js`), et tout ce que l'ancienne fiche ouvrait en écrans
 * empilés, réunis (Wajih, 02/11, piste A du round 2) : un en-tête qui reste (`<TutorStudentHeader>`),
 * puis cinq onglets soulignés, chacun avec ce que le masteur y fait aujourd'hui :
 *  - Aperçu (`<TutorStudentOverview>`) : la prochaine séance d'abord, le reste replié ;
 *  - Activités, Rapports (le diagnostic en tête), Contrôles, Messages : leurs pages, sans en-tête.
 *
 * À l'étroit, hors de l'Aperçu, l'en-tête se resserre pour rendre la hauteur à son onglet.
 *
 * Une seule fiche, trois contenants : poussée au téléphone et sur une tablette (`onBack`), à droite de
 * Mes élèves au large (`<TutorStudentsTwoPane>`, sans retour), et dans le live
 * (`<TutorLiveStudentPanel>` : `live`, `top`, sans Messages). La fiche d'une activité reste une
 * feuille, montée par l'écran.
 *
 * Les blocs réservés à l'admin (« Santé élève », « Demande d'essai ») ne sont pas repris : ils vivent
 * au BO (voir l'audit de parité).
 *
 * Contrôlée : l'onglet, la fiche (nulle tant qu'elle charge), le brief qui arrive et l'état de chaque
 * onglet arrivent en props.
 */
import React, { useContext, useMemo, useState } from "react"
import { Dimensions, type LayoutChangeEvent, SafeAreaView, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { DimensionsContext } from "../../../common/utils/mobileUtils"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { UnderlineTabs, type UnderlineTab } from "../../molecules/UnderlineTabs"
import { TutorStudentHeader } from "../../organisms/TutorStudentHeader"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { PaneWidthContext } from "../../templates/PageTemplate/PageTemplate"
import { TutorStudentOverview } from "../../templates/TutorStudentOverview"
import {
    StudentAssessmentsPage,
    type StudentAssessmentsPageProps,
} from "../StudentAssessmentsPage/StudentAssessmentsPage"
import { TutorConversationPage, type TutorConversationPageProps } from "../TutorConversationPage"
import { TutorStudentActivitiesPage, type TutorStudentActivitiesPageProps } from "../TutorStudentActivitiesPage"
import { TutorStudentReportsPage, type TutorStudentReportsPageProps } from "../TutorStudentReportsPage"
import { type UITutorStudentProfile, type UITutorStudentTab } from "../../../ports/primary/ui/types/tutorEleves"
import { TutorStudentProfileSkeleton } from "./TutorStudentProfileSkeleton"

/** Au-delà, l'en-tête garde tout, sur chaque onglet. */
const HEADER_FULL_FROM = 720

export type TutorStudentProfilePageProps = {
    profile: UITutorStudentProfile | null
    tab: UITutorStudentTab
    onChangeTab: (tab: UITutorStudentTab) => void
    /** Le retour. Absent : la fiche tient dans un volet. */
    onBack?: () => void
    /** Ce que le retour rend. Défaut « Mes élèves » ; l'historique des lives dit le sien. */
    backLabel?: string
    /** Au-dessus de la fiche, à la place du retour : la tête du panneau du live. */
    top?: React.ReactNode
    /** « Activité » de l'en-tête : générer une activité pour cet élève. Absent : pas de bouton. */
    onGenerate?: () => void
    /** La fiche dans le live : « Pour ce live ». Défaut `false`. */
    live?: boolean

    // ── Aperçu ──
    briefLoading?: boolean
    onOpenStudyProgram?: () => void
    /** Pour une story : « Son dossier » ouvert. */
    defaultDossierExpanded?: boolean

    // ── Les autres onglets ──
    // Absents, leur onglet n'est pas là : « Son profil » dans la salle du live (`TutorLiveStudentPanel`)
    // rend l'Aperçu seul.
    activities?: TutorStudentActivitiesPageProps
    reports?: TutorStudentReportsPageProps
    assessments?: Omit<StudentAssessmentsPageProps, "hideHeader" | "title" | "onClose">
    /** La conversation. Absente : pas d'onglet Messages (le live a son chat). */
    messages?: TutorConversationPageProps

    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function TutorStudentProfilePage(props: TutorStudentProfilePageProps) {
    const {
        profile,
        tab,
        onChangeTab,
        onBack,
        backLabel = "Mes élèves",
        top,
        onGenerate,
        live = false,
        briefLoading = false,
        onOpenStudyProgram,
        defaultDossierExpanded,
        activities,
        reports,
        assessments,
        messages,
        loading = false,
        error,
        onRetry,
        testID,
    } = props
    // La largeur mesurée, parce que les pages de ses onglets s'y posent comme dans un volet.
    const [width, setWidth] = useState(0)
    const [headHeight, setHeadHeight] = useState(0)
    const insets = useSafeAreaInsets()
    const onLayout = (event: LayoutChangeEvent) => setWidth(event.nativeEvent.layout.width)
    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")
    const reportsDimensions = useMemo(
        () => ({ ...dimensions, width: width > 0 ? width : dimensions.width }),
        [dimensions, width]
    )
    const wide = width >= HEADER_FULL_FROM
    const generate =
        onGenerate != null ? <Button label="Activité" iconName="plus" size="sm" onPress={onGenerate} /> : null

    const head =
        top ??
        (onBack != null ? (
            <Row justify="between" align="center">
                <Button
                    label={backLabel}
                    iconName="chevron-left"
                    variant="ghost"
                    color="primary"
                    size="sm"
                    onPress={onBack}
                />
                {!wide && generate}
            </Row>
        ) : null)

    if (profile == null) {
        return (
            <PageTemplate
                testID={testID}
                header={head != null ? <View style={styles.bare}>{head}</View> : undefined}
                loading={loading}
                skeleton={<TutorStudentProfileSkeleton />}
                error={error}
                onRetry={onRetry}
                padding="4"
                maxContentWidth={PARENT_CONTENT_WIDTH}
            >
                {null}
            </PageTemplate>
        )
    }

    const tabs: UnderlineTab<UITutorStudentTab>[] = [{ key: "overview", label: "Aperçu" }]
    // Un onglet qui n'a pas lu (ou dont la lecture a échoué) ne dit pas « 0 » : son compte attend sa lecture.
    const unread = (section: { loading?: boolean; error?: unknown }) =>
        section.loading === true || section.error != null
    if (activities != null) {
        tabs.push({
            key: "activities",
            label: "Activités",
            count: unread(activities) ? undefined : activities.activities?.length,
        })
    }
    if (reports != null) tabs.push({ key: "reports", label: "Rapports", count: profile.student.liveReportsCount })
    if (assessments != null) {
        tabs.push({
            key: "assessments",
            label: "Contrôles",
            count: unread(assessments) ? undefined : assessments.assessments.length,
        })
    }
    if (messages != null) {
        tabs.push({
            key: "messages",
            label: "Messages",
            dot: tab !== "messages" && (profile.student.unreadMessagesCount ?? 0) > 0,
        })
    }
    // Un onglet retenu d'un autre contenant (Messages, en live) : l'Aperçu plutôt qu'un corps vide.
    const shownTab = tabs.some((item) => item.key === tab) ? tab : "overview"

    return (
        <SafeAreaView style={styles.root} onLayout={onLayout} testID={testID}>
            <View style={styles.head} onLayout={(event) => setHeadHeight(event.nativeEvent.layout.height)}>
                {head}
                <TutorStudentHeader
                    profile={profile}
                    compact={!wide && shownTab !== "overview"}
                    dense={!wide}
                    trailing={wide || onBack == null ? generate : undefined}
                />
                {tabs.length > 1 && <UnderlineTabs tabs={tabs} value={shownTab} onChange={onChangeTab} />}
            </View>
            <View style={styles.body}>
                <PaneWidthContext.Provider value={width > 0 ? width : null}>
                    {shownTab === "overview" && (
                        <PageTemplate padding="4" maxContentWidth={PARENT_CONTENT_WIDTH}>
                            <TutorStudentOverview
                                profile={profile}
                                live={live}
                                briefLoading={briefLoading}
                                onOpenStudyProgram={onOpenStudyProgram}
                                onOpenReports={reports != null ? () => onChangeTab("reports") : undefined}
                                onOpenAssessments={assessments != null ? () => onChangeTab("assessments") : undefined}
                                defaultDossierExpanded={defaultDossierExpanded}
                            />
                        </PageTemplate>
                    )}
                    {shownTab === "activities" && activities != null && <TutorStudentActivitiesPage {...activities} />}
                    {/* Les cartes de rapport lisent leur largeur là : seulement ici, les feuilles des autres
                        onglets se calent sur la fenêtre. */}
                    {shownTab === "reports" && reports != null && (
                        <DimensionsContext.Provider value={reportsDimensions}>
                            <TutorStudentReportsPage {...reports} />
                        </DimensionsContext.Provider>
                    )}
                    {shownTab === "assessments" && assessments != null && (
                        <StudentAssessmentsPage {...assessments} hideHeader />
                    )}
                    {shownTab === "messages" && messages != null && (
                        <TutorConversationPage {...messages} keyboardOffset={headHeight + insets.top} />
                    )}
                </PaneWidthContext.Provider>
            </View>
        </SafeAreaView>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0, backgroundColor: theme.colors.surface },
    head: {
        gap: theme.spacing["3"],
        paddingTop: theme.spacing["3"],
        paddingHorizontal: theme.spacing["4"],
    },
    bare: { paddingTop: theme.spacing["3"], paddingHorizontal: theme.spacing["4"] },
    body: { flex: 1, minHeight: 0 },
}))
